import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@clareefai/ui';
import {
  ArrowLeft, CreditCard, Tag, Gift, Percent, Coins, CalendarDays,
  ShieldCheck, RotateCcw, CheckCircle, Loader2,
  Star, Heart, Zap, Award, Target, Sparkles, Trophy, ThumbsUp,
  Smile, Coffee, Lightbulb, Music, Mic, BookOpen, MessageCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { RewardConfig } from '@clareefai/types';
import {
  useGetRewardSettingsQuery,
  useUpdateRewardSettingsMutation,
  useGetVendorMeQuery,
} from '../../store/vendorApi';

const ICON_OPTIONS: { name: string; icon: LucideIcon }[] = [
  { name: 'star', icon: Star },
  { name: 'heart', icon: Heart },
  { name: 'zap', icon: Zap },
  { name: 'award', icon: Award },
  { name: 'gift', icon: Gift },
  { name: 'target', icon: Target },
  { name: 'sparkles', icon: Sparkles },
  { name: 'trophy', icon: Trophy },
  { name: 'thumbsup', icon: ThumbsUp },
  { name: 'smile', icon: Smile },
  { name: 'coffee', icon: Coffee },
  { name: 'lightbulb', icon: Lightbulb },
  { name: 'music', icon: Music },
  { name: 'mic', icon: Mic },
  { name: 'bookopen', icon: BookOpen },
  { name: 'messagecircle', icon: MessageCircle },
];

const COLOR_OPTIONS = [
  'bg-blue-500',
  'bg-purple-500',
  'bg-red-500',
  'bg-green-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-indigo-500',
  'bg-teal-500',
];

const CreateCustomReward: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const buildDiscountName = (discountType: string, discountValue: number | null, productName: string): string | null => {
    if (!discountValue) return null;
    const productSuffix = productName ? t('rewards.create.onProductSuffix', { product: productName }) : '';
    if (discountType === 'percentage') return t('rewards.create.percentageDiscountName', { value: discountValue, suffix: productSuffix });
    if (discountType === 'credit_points') return t('rewards.create.creditPointsName', { value: discountValue, suffix: productSuffix });
    const key = discountValue !== 1 ? 'rewards.create.freeMonthsNamePlural' : 'rewards.create.freeMonthsName';
    return t(key, { value: discountValue, suffix: productSuffix });
  };
  const { data: rewardSettings, refetch: refetchSettings } = useGetRewardSettingsQuery();
  const { data: vendor } = useGetVendorMeQuery();
  const [updateRewardSettings] = useUpdateRewardSettingsMutation();

  const currency = rewardSettings?.currency || 'EUR';
  const currencySymbol = currency === 'EUR' ? '\u20AC' : currency === 'GBP' ? '\u00A3' : '$';
  const products = vendor?.pageInfo?.products?.filter((p: any) => p.name.trim()) || [];

  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedName, setSavedName] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('star');
  const [selectedColor, setSelectedColor] = useState('bg-purple-500');
  const [rewardMethod, setRewardMethod] = useState('GIFT_CARD');
  const [amount, setAmount] = useState<number | null>(null);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState<number | null>(null);
  const [discountProductName, setDiscountProductName] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [maxTimes, setMaxTimes] = useState(1);

  const showGiftCard = rewardMethod === 'GIFT_CARD' || rewardMethod === 'BOTH';
  const showDiscount = rewardMethod === 'DISCOUNT' || rewardMethod === 'BOTH';

  const getIconComponent = (iconName: string) => {
    return ICON_OPTIONS.find(o => o.name === iconName)?.icon || Star;
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t('rewards.create.enterName'));
      return;
    }
    if (!description.trim()) {
      toast.error(t('rewards.create.enterDescription'));
      return;
    }

    setIsSaving(true);
    try {
      const newId = `custom_${crypto.randomUUID()}`;
      const newConfig: RewardConfig = {
        id: newId,
        type: 'CUSTOM',
        name: name.trim(),
        description: description.trim(),
        icon: selectedIcon,
        color: selectedColor,
        is_active: true,
        auto_approve: autoApprove,
        max_times: maxTimes,
        amount: showGiftCard ? (amount || 0) : 0,
        reward_method: rewardMethod,
        discount_type: showDiscount ? discountType : null,
        discount_value: showDiscount ? discountValue : null,
        discount_product_name: showDiscount ? (discountProductName.trim() || null) : null,
        discount_name: showDiscount ? buildDiscountName(discountType, discountValue, discountProductName) : null,
      };

      const currentConfigs = rewardSettings?.reward_configs || [];
      await updateRewardSettings({
        currency,
        reward_configs: [...currentConfigs, newConfig],
      }).unwrap();

      refetchSettings();
      setSavedName(name.trim());
      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to create custom reward:', error);
      toast.error(t('rewards.create.failedToCreate'));
    } finally {
      setIsSaving(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('rewards.create.successTitle')}</h2>
          <p
            className="text-gray-600 mb-6"
            dangerouslySetInnerHTML={{
              __html: t('rewards.create.successDesc', { name: savedName }).replace('<span>', '<span class="font-semibold">'),
            }}
          />
          <button
            onClick={() => navigate('/rewards')}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            {t('rewards.create.backToRewards')}
          </button>
        </div>
      </div>
    );
  }

  const PreviewIcon = getIconComponent(selectedIcon);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back link */}
      <button
        onClick={() => navigate('/rewards')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">{t('rewards.create.backToRewards')}</span>
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('rewards.create.title')}</h1>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Live Preview */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{t('rewards.create.preview')}</p>
          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 ${selectedColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <PreviewIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900">{name || t('rewards.create.rewardNamePreview')}</h3>
                <p className="text-sm text-gray-500 mt-0.5">{description || t('rewards.create.descriptionPreview')}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {showGiftCard && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-lg font-bold bg-emerald-100 text-emerald-700">
                      <CreditCard className="w-4 h-4" />
                      {amount || 0} {currency}
                    </span>
                  )}
                  {rewardMethod === 'BOTH' && discountValue && (
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{t('rewards.or')}</span>
                  )}
                  {showDiscount && discountValue && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-lg font-bold bg-orange-100 text-orange-700">
                      {discountType === 'percentage' && <Percent className="w-4 h-4" />}
                      {discountType === 'credit_points' && <Coins className="w-4 h-4" />}
                      {discountType === 'free_months' && <CalendarDays className="w-4 h-4" />}
                      {discountType === 'percentage'
                        ? t('rewards.discount.percentOff', { value: discountValue })
                        : discountType === 'credit_points'
                          ? t('rewards.discount.points', { value: discountValue })
                          : t('rewards.discount.freeMonths', { value: discountValue })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Reward Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.create.rewardName')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={t('rewards.create.rewardNamePlaceholder')}
              maxLength={80}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.create.descriptionLabel')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder={t('rewards.create.descriptionPlaceholder')}
              maxLength={300}
            />
          </div>

          {/* Icon Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.create.iconLabel')}</label>
            <div className="grid grid-cols-8 gap-2">
              {ICON_OPTIONS.map(({ name: iconName, icon: Icon }) => (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => setSelectedIcon(iconName)}
                  className={`p-2.5 rounded-lg border-2 transition-all flex items-center justify-center ${
                    selectedIcon === iconName
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${selectedIcon === iconName ? 'text-purple-600' : 'text-gray-500'}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Color Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.create.colorLabel')}</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className={`w-9 h-9 rounded-full ${color} transition-all ${
                    selectedColor === color
                      ? 'ring-2 ring-offset-2 ring-purple-500 scale-110'
                      : 'hover:scale-105'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Reward Method */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
              <Gift className="w-5 h-5 mr-2 text-purple-600" />
              {t('rewards.create.rewardMethod')}
            </h3>

            <div className="grid grid-cols-3 gap-2 mb-5">
              {[
                { value: 'GIFT_CARD', label: t('rewards.create.methodGiftCard'), icon: CreditCard, desc: t('rewards.create.methodGiftCardDesc') },
                { value: 'DISCOUNT', label: t('rewards.create.methodDiscount'), icon: Tag, desc: t('rewards.create.methodDiscountDesc') },
                { value: 'BOTH', label: t('rewards.create.methodBoth'), icon: Gift, desc: t('rewards.create.methodBothDesc') },
              ].map(({ value, label, icon: Icon, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRewardMethod(value)}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    rewardMethod === value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`w-5 h-5 mx-auto mb-1 ${rewardMethod === value ? 'text-purple-600' : 'text-gray-400'}`} />
                  <div className={`text-sm font-medium ${rewardMethod === value ? 'text-purple-700' : 'text-gray-700'}`}>{label}</div>
                  <div className="text-xs text-gray-500">{desc}</div>
                </button>
              ))}
            </div>

            {/* Gift Card Amount */}
            {showGiftCard && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {rewardMethod === 'BOTH' ? t('rewards.create.giftCardAmountBoth') : t('rewards.create.amount')} ({currency})
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">
                    {currencySymbol}
                  </span>
                  <input
                    type="number"
                    value={amount ?? ''}
                    onChange={(e) => setAmount(e.target.value === '' ? null : parseInt(e.target.value) || 0)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="0"
                    min="0"
                    step="10"
                  />
                </div>
              </div>
            )}

            {/* Discount Configuration */}
            {showDiscount && (
              <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="text-sm font-medium text-purple-900">{t('rewards.create.discountConfig')}</h4>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('rewards.create.discountType')}</label>
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                    {[
                      { value: 'percentage', label: t('rewards.create.percentage'), icon: Percent },
                      { value: 'credit_points', label: t('rewards.create.creditPoints'), icon: Coins },
                      { value: 'free_months', label: t('rewards.create.freeMonths'), icon: CalendarDays },
                    ].map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setDiscountType(value);
                          if (value === 'free_months' && discountValue) {
                            setDiscountValue(Math.max(1, Math.floor(discountValue)));
                          }
                        }}
                        className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs font-medium transition-colors ${
                          discountType === value
                            ? 'bg-purple-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        } ${value !== 'percentage' ? 'border-l border-gray-300' : ''}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {discountType === 'percentage' ? t('rewards.create.discountValue') : discountType === 'credit_points' ? t('rewards.create.creditPointsLabel') : t('rewards.create.numberOfMonths')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                      {discountType === 'percentage' ? '%' : discountType === 'credit_points' ? 'pts' : 'mo'}
                    </span>
                    <input
                      type="number"
                      value={discountValue ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          setDiscountValue(null);
                        } else {
                          const num = discountType === 'free_months'
                            ? Math.max(0, Math.floor(parseFloat(val) || 0))
                            : parseFloat(val);
                          setDiscountValue(num);
                        }
                      }}
                      className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                      max={discountType === 'percentage' ? 100 : undefined}
                      step={discountType === 'free_months' ? 1 : undefined}
                    />
                  </div>
                </div>

                {products.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('rewards.create.productOptional')}</label>
                    <select
                      value={discountProductName}
                      onChange={(e) => setDiscountProductName(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    >
                      <option value="">{t('rewards.create.noSpecificProduct')}</option>
                      {products.map((product: any) => (
                        <option key={product.name} value={product.name}>{product.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {discountValue && (
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <span className="text-sm text-purple-800 font-medium">
                      {t('rewards.create.discountPreview', { label: buildDiscountName(discountType, discountValue, discountProductName) })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {rewardMethod === 'BOTH' && (
              <p className="text-xs text-gray-500 mt-3">
                {t('rewards.create.bothHelp')}
              </p>
            )}
          </div>

          {/* Approval & Limits */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
              <ShieldCheck className="w-5 h-5 mr-2 text-blue-600" />
              {t('rewards.create.approvalLimits')}
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="font-medium text-gray-900">{t('rewards.create.autoApproveLabel')}</label>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {autoApprove
                      ? t('rewards.create.autoApproveOn')
                      : t('rewards.create.autoApproveOff')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoApprove}
                  onClick={() => setAutoApprove(!autoApprove)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    autoApprove ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoApprove ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="font-medium text-gray-900 flex items-center">
                      <RotateCcw className="w-4 h-4 mr-2 text-gray-500" />
                      {t('rewards.create.maxTimesLabel')}
                    </label>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {t('rewards.create.maxTimesHelp')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 mt-3">
                  <input
                    type="number"
                    value={maxTimes}
                    onChange={(e) => setMaxTimes(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-24 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    min="1"
                  />
                  <span className="text-sm text-gray-500">
                    {t('rewards.create.limitedToTimes', { count: maxTimes })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={() => navigate('/rewards')}
            className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium border border-gray-300"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim() || !description.trim()}
            className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('rewards.create.creating')}</span>
              </>
            ) : (
              <span>{t('rewards.create.createButton')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCustomReward;
